import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { Plus, Eye, Edit, Trash2, User, Languages, Clock, Filter, TrendingUp, GraduationCap, Briefcase, Target, ExternalLink, ChevronUp, ChevronDown, Activity, BookOpen, Merge } from 'lucide-react'
import toast from 'react-hot-toast'
import { talents as talentService, libraries, statuses, stages, users, getUsedCityIds } from '../../services/apiService'
import { useAuth } from '../../hooks/useAuth'
import { getTalentTypeLabel, getTalentTypeIcon, getTalentTypeOptions, getTalentTypeColor, getStatusTypes } from '../../utils/talentTypeUtils'
import { getToolIcon } from '../../utils/toolIconUtils.jsx'
import { calculateIsActive } from '../../utils/talentStatusUtils'
import { getNoteTypesForSelect } from '../../utils/noteTypeUtils'
import { getProfessionName, getPositionName, getSimilarTermName } from '../../utils/professionUtils'
import { MEDIA_TYPES, MEDIA_TYPE_LABELS } from '../../utils/mediaTypeUtils'
import { Table, Toggle, TextField, Autocomplete } from '@anyemp/ui-kit'
import { loadColumnsStateFromLS, useTableColumns, insertColumnAfter, insertColumnBefore } from '../../utils/tableColumnsPersistence'
import HistoryModal from '../UI/HistoryModal'
import ConfirmDialog from '../UI/ConfirmDialog'
import StatusTransitionModal from '../StatusManagement/StatusTransitionModal'
import StageTransitionModal from '../StageManagement/StageTransitionModal'
import SearchInput from '../UI/SearchInput'
import StatusSelect from '../UI/StatusSelect'
import StageSelect from '../UI/StageSelect'
import ManagerSelect from '../UI/ManagerSelect'
import UserSelect from '../UI/UserSelect'
import Avatar from '../UI/Avatar'
import ProfileLink from '../UI/ProfileLink'
import TalentExport from './TalentExport'
import MergeTalentModal from './MergeTalentModal'
import TalentTypeChangeModal from './TalentTypeChangeModal'
import DateRangePicker from '../UI/DateRangePicker'
import { getDateTypesForSelect } from '../../utils/dateTypeUtils'
import { formatApiError } from '../../utils/formatApiError'
import ActivityFitPanel from '../UI/ActivityFitPanel'
import SavedFiltersDropdown from '../SavedFilters/SavedFiltersDropdown'
import { PAGE_KEYS } from '../../constants/savedFilterPageKeys'
import FavoriteStar from '../Favorites/FavoriteStar'
import FavoritesFilterDropdown from '../Favorites/FavoritesFilterDropdown'
import { useFavorites } from '../../context/FavoritesContext'
import useResponsiveViewPreference from '../../hooks/useResponsiveViewPreference'
import { getTalentListFamily, VIEW_MODE } from '../../utils/viewPreference'
import PageHeader from '../UI/PageHeader'
import DataViewToggle from '../UI/DataViewToggle'
import CompactCardSort from './CompactCardSort'
import TalentCard from './TalentCard'
import TalentListPagination from './TalentListPagination'
import { getTalentCardSortOptions } from '../../utils/cardSortOptions'
import {
  getDefaultTalentCardSort,
  getEffectiveTalentCardSort,
  scheduleResultsScrollToStart,
} from '../../utils/talentListInteractions'

const SAVED_FILTER_KEYS = [
  'search', 'status_id', 'stage_id', 'manager_id', 'profession_filter', 'department_id',
  'tool_id', 'language_id', 'country_id', 'city_id', 'contact_tool_id',
  'talent_type', 'is_active', 'current_only', 'hide_refusals',
  'note_type', 'note_search',
  'important_date_type', 'important_date_from', 'important_date_to',
  'age_from', 'age_to',
  'created_from', 'created_to', 'updated_from', 'updated_to',
  'favorites_of', 'favorite_list_id',
]

// Статусы-«отказы» — тот же набор, что EXIT_STATUSES в hrRecruitmentStatsService.js
// (сквозные статусы, применимые на любой стадии воронки). Скрываются по
// умолчанию на стадийных страницах кандидатов (см. hide_refusals ниже).
const REFUSAL_STATUS_NAMES = ['Denied', 'Refused', 'Not Approved', 'Blacklist', 'Not relevant for us']

const TALENT_TYPE_CONFIG = {
  all: {
    title: 'All Talents',
    description: 'Complete list of all talents with their statuses and history',
    addButtonText: 'Add Talent',
    listTitle: 'All Talents List',
    emptyIcon: User,
    emptyText: 'No talents found',
    columnLabel: 'Talent',
    typeTransitions: {}
  },
  candidate: {
    title: 'Candidates',
    description: 'Manage candidates - everyone who was once a candidate (including current and historical)',
    addButtonText: 'Add Candidate',
    listTitle: 'Candidates List',
    emptyIcon: User,
    emptyText: 'No candidates found',
    columnLabel: 'Candidate',
    typeTransitions: {
      trainee: { label: 'Move to Trainee', icon: BookOpen, reason: 'Candidate moved to trainee' },
      employee: { label: 'Hire as Employee', icon: Briefcase, reason: 'Hired as employee' },
      presale: { label: 'Promote to Presale', icon: TrendingUp, reason: 'Promoted to presale' }
    }
  },
  trainee: {
    title: 'Trainees',
    description: 'Manage trainees - candidates in the process of onboarding before becoming employees',
    addButtonText: 'Add Trainee',
    listTitle: 'Trainees List',
    emptyIcon: BookOpen,
    emptyText: 'No trainees found',
    columnLabel: 'Trainee',
    typeTransitions: {
      employee: { label: 'Promote to Employee', icon: Briefcase, reason: 'Trainee promoted to employee' },
      presale: { label: 'Promote to Presale', icon: TrendingUp, reason: 'Trainee promoted to presale' },
      candidate: { label: 'Return to Candidate', icon: GraduationCap, reason: 'Trainee returned to candidate' }
    }
  },
  employee: {
    title: 'Employees',
    description: 'Manage employees with API /type/employee and statuses for employee type',
    addButtonText: 'Add Employee',
    listTitle: 'Employees List',
    emptyIcon: Briefcase,
    emptyText: 'No employees found',
    columnLabel: 'Employee',
    typeTransitions: {
      presale: { label: 'Promote to Presale', icon: TrendingUp, reason: 'Promoted to presale' },
      trainee: { label: 'Demote to Trainee', icon: BookOpen, reason: 'Employee demoted to trainee' },
      candidate: { label: 'Demote to Candidate', icon: GraduationCap, reason: 'Demoted to candidate' }
    }
  },
  presale: {
    title: 'Presale',
    description: 'Manage presale talents with API /type/presale and statuses for presale type',
    addButtonText: 'Add Presale',
    listTitle: 'Presale Talents List',
    emptyIcon: Target,
    emptyText: 'No presale talents found',
    columnLabel: 'Presale Talent',
    typeTransitions: {
      employee: { label: 'Move to Employee', icon: Briefcase, reason: 'Moved from presale to employee' },
      trainee: { label: 'Move to Trainee', icon: BookOpen, reason: 'Moved from presale to trainee' },
      candidate: { label: 'Move to Candidate', icon: GraduationCap, reason: 'Moved from presale to candidate' }
    }
  }
}

// Keys that should be reflected in the URL so back-navigation and shareable
// links restore the same view. include_relations is constant and excluded.
const URL_FILTER_KEYS = [
  'search', 'status_id', 'stage_id', 'manager_id', 'profession_filter', 'department_id',
  'tool_id', 'language_id', 'country_id', 'city_id', 'contact_tool_id',
  'talent_type', 'is_active', 'current_only', 'hide_refusals',
  'note_type', 'note_search',
  'important_date_type', 'important_date_from', 'important_date_to',
  'age_from', 'age_to',
  'created_from', 'created_to', 'updated_from', 'updated_to',
  'favorites_of', 'favorite_list_id',
  'page', 'limit', 'sort_by', 'sort_order',
]

// Filter keys whose values are numeric IDs — must be cast to Number
// when read from URL so <Select> components match their numeric options.
// manager_id (строка из auth), note_type и important_date_type (строковые коды)
// сюда НЕ входят: parseInt превращал их в NaN, и фильтр из URL не восстанавливался.
const NUMERIC_FILTER_KEYS = new Set([
  'status_id', 'stage_id', 'department_id', 'tool_id', 'language_id',
  'country_id', 'city_id', 'contact_tool_id',
  'age_from', 'age_to',
  'favorite_list_id',
  'page', 'limit',
])

// Фильтры с множественным выбором: в состоянии хранятся массивами,
// в URL и в запросе — строкой через запятую. Одиночное значение в URL
// (старые ссылки вида ?status_id=5) читается как список из одного элемента.
const MULTI_FILTER_KEYS = new Set([
  'status_id', 'stage_id', 'manager_id', 'profession_filter', 'department_id',
  'tool_id', 'language_id', 'country_id', 'city_id', 'contact_tool_id',
  'talent_type', 'note_type', 'important_date_type', 'favorite_list_id',
])

/**
 * Приводит множественные фильтры к массивам.
 * Нужно для фильтров, сохранённых до мультивыбора: там значения лежат скалярами,
 * а MultiSelect ждёт массив.
 */
const normalizeMultiFilters = (saved) => {
  const out = { ...saved }
  for (const key of MULTI_FILTER_KEYS) {
    if (!(key in out)) continue
    const v = out[key]
    if (Array.isArray(v)) continue
    out[key] = (v === '' || v === null || v === undefined) ? [] : [v]
  }
  return out
}

const buildDefaultFilters = (defaultFilters) => ({
  search: '',
  // Множественные — массивы
  status_id: [],
  stage_id: [],
  manager_id: [],
  profession_filter: [],
  department_id: [],
  tool_id: [],
  language_id: [],
  country_id: [],
  city_id: [],
  contact_tool_id: [],
  talent_type: defaultFilters.talent_type ? [defaultFilters.talent_type] : [],
  note_type: [],
  important_date_type: [],
  is_active: defaultFilters.is_active !== undefined ? defaultFilters.is_active : '',
  current_only: defaultFilters.current_only !== undefined ? defaultFilters.current_only : '',
  // Скрывать отказы (Denied/Refused/...) по умолчанию — имеет эффект только
  // на стадийных страницах (stageKeys), см. использование ниже. 'true', а не
  // булево: тот же способ хранения, что у is_active/current_only, — из URL
  // приходит строкой.
  hide_refusals: defaultFilters.hide_refusals !== undefined ? defaultFilters.hide_refusals : 'true',
  note_search: '',
  important_date_from: '',
  important_date_to: '',
  age_from: '',
  age_to: '',
  created_from: '',
  created_to: '',
  updated_from: '',
  updated_to: '',
  favorites_of: defaultFilters.favorites_of ?? '',
  favorite_list_id: defaultFilters.favorite_list_id ? [defaultFilters.favorite_list_id] : [],
  page: 1,
  limit: 20,
  sort_by: defaultFilters.sort_by ?? '',
  sort_order: defaultFilters.sort_order ?? 'ASC',
  include_relations: 'true',
})

const readFiltersFromSearchParams = (searchParams, defaultFilters) => {
  const base = buildDefaultFilters(defaultFilters)
  for (const key of URL_FILTER_KEYS) {
    if (!searchParams.has(key)) continue
    const raw = searchParams.get(key)

    if (MULTI_FILTER_KEYS.has(key)) {
      // Одно значение или список через запятую — и то и другое даёт массив,
      // поэтому старые ссылки с одним значением продолжают работать
      const parts = raw.split(',').map(s => s.trim()).filter(Boolean)
      const values = NUMERIC_FILTER_KEYS.has(key)
        ? parts.map(s => parseInt(s, 10)).filter(n => !Number.isNaN(n) && n > 0)
        : parts
      if (values.length > 0) base[key] = values
      continue
    }

    if (NUMERIC_FILTER_KEYS.has(key)) {
      const n = parseInt(raw, 10)
      if (!Number.isNaN(n) && n > 0) base[key] = n
    } else {
      base[key] = raw
    }
  }
  return base
}

const filtersToSearchParams = (filters) => {
  const params = new URLSearchParams()
  for (const key of URL_FILTER_KEYS) {
    const v = filters[key]
    if (v === '' || v === null || v === undefined) continue
    if (Array.isArray(v)) {
      if (v.length === 0) continue
      params.set(key, v.join(','))
      continue
    }
    if (key === 'page' && v === 1) continue
    if (key === 'limit' && v === 20) continue
    if (key === 'sort_order' && v === 'ASC' && !filters.sort_by) continue
    if (key === 'hide_refusals' && v === 'true') continue // дефолт, не засоряет URL
    params.set(key, String(v))
  }
  return params
}

// Колонка звезды подписана пустой меткой, поэтому её ключ 'favorite',
// а параметр API — говорящий 'favorited_at'. Маппинг нужен в обе стороны:
// handleSort переводит ключ колонки в параметр, а Table сравнивает sort_by
// с ключом колонки, чтобы нарисовать стрелку.
const SORT_KEY_BY_COLUMN = { favorite: 'favorited_at' }
const COLUMN_BY_SORT_KEY = { favorited_at: 'favorite' }

const MEDIA_TYPE_COLUMNS = Object.values(MEDIA_TYPES).map((typeValue) => ({
  key: `media_${typeValue}`,
  label: MEDIA_TYPE_LABELS[typeValue] || typeValue,
  sortable: false,
}))

const TalentListPage = ({
  talentType = 'candidate',
  statusNames = null,
  stageKeys = null,
  statusTransitionFrom = null,
  statusTransitionTo = null,
  pageTitle = null,
  pageDescription = null,
  defaultFilters = {},
  useAllEmployeesEndpoint = false,
  // Заданы страницей «Избранное»: тогда селекты избранного в тулбаре скрыты,
  // ими управляет шапка страницы.
  favoritesOf = null,
  favoriteListId = null,
  // Дополнительные контролы страницы под заголовком (страница «Избранное»
  // кладёт сюда выбор владельца и списка)
  headerExtra = null
}) => {
  const config = TALENT_TYPE_CONFIG[talentType]
  const { hasPermission, user } = useAuth()
  const { lookupTalents } = useFavorites()
  const location = useLocation()
  const viewFamily = useMemo(
    () => getTalentListFamily({ talentType, pathname: location.pathname }),
    [talentType, location.pathname]
  )
  const { mode: viewMode, selectMode: setViewMode } = useResponsiveViewPreference(viewFamily)

  // Страница «Избранное» задаёт эти фильтры сама — в тулбаре их дублировать не нужно
  const isFavoritesPage = favoritesOf !== null || favoriteListId !== null
  const canReadForeignFavorites = hasPermission('talent-service', 'talent_favorite', 'read-all')
  const [searchParams, setSearchParams] = useSearchParams()

  // Колонки зарплаты — только с правом <type>_salary.read. На общей странице
  // (all / избранное) строки разных типов: достаточно права хоть на один,
  // бэкенд всё равно отдаст зарплату только по разрешённым типам
  const canReadSalary = talentType === 'all'
    ? getTalentTypeOptions().some(({ value }) => hasPermission('talent-service', `${value}_salary`, 'read'))
    : hasPermission('talent-service', `${talentType}_salary`, 'read')
  // { [currency_id]: { name, symbol } } — в списке валюты приходят только id
  const [currencyById, setCurrencyById] = useState({})

  useEffect(() => {
    if (!canReadSalary) return
    let cancelled = false
    libraries.getCurrencies()
      .then((res) => {
        if (cancelled) return
        const list = Array.isArray(res?.data) ? res.data : []
        setCurrencyById(Object.fromEntries(list.map((c) => [c.id, c])))
      })
      .catch((err) => console.error('Error loading currencies:', err))
    return () => { cancelled = true }
  }, [canReadSalary])

  // Override title and description if provided
  const displayTitle = pageTitle || config.title
  const displayDescription = pageDescription || config.description



  const [isActivityFitOpen, setIsActivityFitOpen] = useState(false)
  const [talents, setTalents] = useState([])
  const [tableLoading, setTableLoading] = useState(true)
  const [error, setError] = useState(null)
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 20
  })
  const [historyModalOpen, setHistoryModalOpen] = useState(false)
  const [selectedTalent, setSelectedTalent] = useState(null)
  const [showFilters, setShowFilters] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  // { talent, type, reason } — открытый диалог перевода в другой тип
  const [pendingTypeChange, setPendingTypeChange] = useState(null)

  const [deleteConfirm, setDeleteConfirm] = useState(null) // talent to delete
  const [mergeTarget, setMergeTarget] = useState(null) // talent to keep when merging a duplicate

  // Status transition modal state
  const [showTransitionModal, setShowTransitionModal] = useState(false)
  const [transitionData, setTransitionData] = useState(null)

  // Status editing state
  const [editingStatusId, setEditingStatusId] = useState(null)
  const [statusChangeLoading, setStatusChangeLoading] = useState(null)

  // Stage transition modal state — отдельная ось от статуса
  const [showStageTransitionModal, setShowStageTransitionModal] = useState(false)
  const [stageTransitionData, setStageTransitionData] = useState(null)

  // Stage editing state
  const [editingStageId, setEditingStageId] = useState(null)
  const [stageChangeLoading, setStageChangeLoading] = useState(null)

  // Active status editing state
  const [activeStatusChangeLoading, setActiveStatusChangeLoading] = useState(null)

  // Manager editing state
  const [editingManagerId, setEditingManagerId] = useState(null)
  const [managerChangeLoading, setManagerChangeLoading] = useState(null)

  // Linked user editing state
  const [editingLinkedUserId, setEditingLinkedUserId] = useState(null)
  const [linkedUserChangeLoading, setLinkedUserChangeLoading] = useState(null)
  const [linkedUserOptions, setLinkedUserOptions] = useState([])
  const [linkedUserOptionsLoading, setLinkedUserOptionsLoading] = useState(false)

  // Filters — initialized from URL search params so back-navigation
  // from a talent detail page restores the same view.
  const [filters, setFilters] = useState(() => readFiltersFromSearchParams(searchParams, defaultFilters))
  const resultsStartRef = useRef(null)
  const defaultCardSort = getDefaultTalentCardSort(talentType !== 'all')

  // Mirror filter state back to the URL (replace, so we don't pollute history).
  useEffect(() => {
    const next = filtersToSearchParams(filters)
    const current = searchParams.toString()
    if (next.toString() !== current) {
      setSearchParams(next, { replace: true })
    }
  }, [filters]) // eslint-disable-line react-hooks/exhaustive-deps

  // URL representation of the current view — used as `state.from` on row links
  // so back-navigation restores filters. Derived from `filters` (not from
  // `location.search`) so it stays in sync even on the render right after a
  // filter change, before the URL mirror effect has run.
  const fromHref = useMemo(() => {
    const qs = filtersToSearchParams(filters).toString()
    return qs ? `${location.pathname}?${qs}` : location.pathname
  }, [filters, location.pathname])

  // Expand/collapse state for chips
  const [expandedChips, setExpandedChips] = useState({
    professions: {},
    tools: {},
    responsibilities: {},
    contacts: {},
    languages: {}
  })

  // Reference data options
  const [statusOptions, setStatusOptions] = useState([])
  const [stageOptions, setStageOptions] = useState([])
  // { [stageId]: statusOption[] } — статусы, реально допустимые на каждой
  // стадии, для выпадающего списка Status в строке. Стадий мало (единицы),
  // поэтому карту дёшево собрать разом при загрузке страницы, вместо
  // ленивой подгрузки по клику на каждую строку
  const [stageStatusesMap, setStageStatusesMap] = useState({})
  const [countryOptions, setCountryOptions] = useState([])
  const [cityOptions, setCityOptions] = useState([])
  const [toolsOptions, setToolsOptions] = useState([])
  const [professionFilterOptions, setProfessionFilterOptions] = useState([])
  const [departmentsOptions, setDepartmentsOptions] = useState([])
  const [languageOptions, setLanguageOptions] = useState([])
  const [userOptions, setUserOptions] = useState([])
  const [contactToolsOptions, setContactToolsOptions] = useState([])
  const [noteTypeOptions, setNoteTypeOptions] = useState([])

  useEffect(() => {
    fetchTalents()
    // statusNames/stageKeys — раньше каждая /candidates/<x> страница была
    // отдельным Route с отдельным элементом, поэтому React Router полностью
    // размонтировал/монтировал TalentListPage заново при переходе между ними
    // — эффект перезапускался сам собой. Теперь это один маршрут
    // /candidates/:stageKey (см. CandidatesByStage.jsx), тот же компонент
    // остаётся смонтированным, меняются только пропсы — без этих зависимостей
    // переход между вкладками молча оставлял бы список от предыдущей стадии.
  }, [filters, talentType, statusNames, stageKeys])

  // Страница «Избранное» управляет этими фильтрами через пропсы
  useEffect(() => {
    if (favoritesOf === null && favoriteListId === null) return
    setFilters(prev => {
      const nextListIds = favoriteListId ? [Number(favoriteListId)] : []
      const sameOwner = (prev.favorites_of || '') === (favoritesOf || '')
      const sameList = prev.favorite_list_id.length === nextListIds.length &&
        prev.favorite_list_id.every((id, i) => id === nextListIds[i])
      if (sameOwner && sameList) return prev
      return { ...prev, favorites_of: favoritesOf || '', favorite_list_id: nextListIds, page: 1 }
    })
  }, [favoritesOf, favoriteListId])

  useEffect(() => {
    fetchReferenceData()
  }, [talentType])

  // Load note types
  useEffect(() => {
    try {
      const noteTypes = getNoteTypesForSelect()
      setNoteTypeOptions(noteTypes)
    } catch (error) {
      console.error('Error loading note types:', error)
      setNoteTypeOptions([])
    }
  }, [])


  const fetchTalents = useCallback(async () => {
    try {
      setTableLoading(true)
      setError(null)

      // Build params from filters, filtering out empty values.
      // Множественные фильтры хранятся массивами — склеиваем через запятую:
      // такой формат уже принят в проекте (status_names, talent_type) и оставляет
      // URL читаемым, а бэкенд одинаково понимает и одно значение, и список
      const params = {
        ...Object.fromEntries(
          Object.entries(filters)
            .filter(([k, value]) => {
              if (k === 'profession_filter') return false
              if (k === 'hide_refusals') return false // не имя параметра бэкенда — переводится в exclude_status_names ниже
              if (Array.isArray(value)) return value.length > 0
              return value !== '' && value !== null && value !== undefined
            })
            .map(([k, value]) => [k, Array.isArray(value) ? value.join(',') : value])
        )
      }

      // Профессии и похожие термины лежат в одном селекте с префиксами,
      // но на бэкенд уходят разными параметрами
      const professionFilters = Array.isArray(filters.profession_filter)
        ? filters.profession_filter
        : (filters.profession_filter ? [filters.profession_filter] : [])
      const professionIds = professionFilters.filter(v => String(v).startsWith('prof_')).map(v => String(v).replace('prof_', ''))
      const similarTermIds = professionFilters.filter(v => String(v).startsWith('sim_')).map(v => String(v).replace('sim_', ''))
      if (professionIds.length > 0) params.profession_id = professionIds.join(',')
      if (similarTermIds.length > 0) params.similar_term_id = similarTermIds.join(',')

      // Add statusNames parameter if provided (comma-separated status names)
      if (statusNames) {
        params.status_names = statusNames
      }

      // Add stageKeys parameter if provided (comma-separated stage keys, e.g. "video")
      if (stageKeys) {
        params.stage_keys = stageKeys
      }

      // Зарплаты в списке — только по запросу; права бэкенд проверяет сам
      if (canReadSalary) {
        params.include_salary = 'true'
      }

      // Скрыть отказы по умолчанию — только на стадийных страницах (stageKeys)
      // и только пока статус не выбран явно фильтром Status: явный выбор
      // статуса (в т.ч. Denied/Refused) важнее фонового дефолта, а не наоборот.
      if (stageKeys && filters.hide_refusals !== 'false' && filters.status_id.length === 0) {
        params.exclude_status_names = REFUSAL_STATUS_NAMES.join(',')
      }

      // Add status transition filters if provided
      if (statusTransitionFrom) {
        params.status_transition_from = statusTransitionFrom
      }
      if (statusTransitionTo) {
        params.status_transition_to = statusTransitionTo
      }

      // Выбираем правильный endpoint в зависимости от режима
      let response
      if (talentType === 'all') {
        // Для режима "all" используем getTalents
        response = await talentService.getTalents(params)
      } else if (useAllEmployeesEndpoint && talentType === 'employee') {
        // Для EmployeesAll используем getAllEmployees
        response = await talentService.getAllEmployees(params)
      } else {
        // Для конкретного типа используем getTalentsByTypeWithHistory
        response = await talentService.getTalentsByTypeWithHistory(talentType, params)
      }

      // Handle different response structures
      let talentsData = []
      if (talentType === 'all') {
        // getTalents returns: response.data.data (array directly)
        if (response && response.success && response.data) {
          talentsData = response.data.data || response.data
        }
      } else {
        // getTalentsByTypeWithHistory and getAllEmployees return: response.data.talents
        if (response && response.data && response.data.talents && Array.isArray(response.data.talents)) {
          talentsData = response.data.talents
        }
      }

      if (Array.isArray(talentsData)) {
        setTalents(talentsData)
        // No-op, пока избранного немного: контекст уже держит все отметки.
        // Догружает точечно только когда полный дамп был бы слишком большим.
        lookupTalents(talentsData.map(t => t.id))

        // Check for pagination data
        const paginationData = response.pagination || response.data.pagination

        if (paginationData) {
          setPagination({
            current_page: paginationData.current_page || filters.page,
            total_pages: paginationData.totalPages || paginationData.total_pages || 1,
            total_items: paginationData.total || 0,
            items_per_page: filters.limit
          })
        } else {
          setPagination({
            current_page: filters.page,
            total_pages: 1,
            total_items: talentsData.length,
            items_per_page: filters.limit
          })
        }
      } else {
        setTalents([])
        setPagination({
          current_page: 1,
          total_pages: 1,
          total_items: 0,
          items_per_page: filters.limit
        })
      }
    } catch (err) {
      console.error(`Error fetching ${talentType}:`, err)
      setError(`Error loading ${talentType}`)
      setTalents([])
      setPagination({
        current_page: 1,
        total_pages: 1,
        total_items: 0,
        items_per_page: filters.limit
      })
    } finally {
      setTableLoading(false)
    }
  }, [filters, talentType, statusNames, stageKeys, statusTransitionFrom, statusTransitionTo, useAllEmployeesEndpoint, lookupTalents, canReadSalary])

  const fetchReferenceData = () => {
    // Каждый запрос независим — запускаем параллельно, каждый селект активируется по готовности своих данных
    const loadStatuses = async () => {
      try {
        const res = talentType === 'all'
          ? await statuses.getAllStatuses({ all: true })
          : await statuses.getStatusesByType(talentType)
        if (res?.success || res?.data) {
          // Сортируем один раз здесь — статусы отсюда идут и в фильтр, и в выпадающий
          // список смены статуса в строке таблицы. По алфавиту искать нужный проще,
          // чем в порядке display_order, который задан под другие сценарии
          const list = Array.isArray(res.data) ? res.data : []
          setStatusOptions(
            [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
          )
        }
      } catch (err) { console.error('❌ Error loading statuses:', err) }
    }

    const loadStages = async () => {
      try {
        // Стадии не зависят от talentType — весь жизненный цикл виден сразу,
        // отфильтровать под конкретную карточку решает StageSelect на месте
        const res = await stages.getStages()
        if (res?.success) {
          const list = Array.isArray(res.data) ? res.data : []
          setStageOptions([...list].sort((a, b) => (a.stage_order || 0) - (b.stage_order || 0)))

          // Статусы, допустимые на каждой стадии — для Status в строке.
          // Порядок внутри стадии оставляем как есть (display_order связки:
          // первый элемент — дефолт стадии), не пересортировываем алфавитно
          const entries = await Promise.all(
            list.map(async (stage) => {
              try {
                const statusRes = await stages.getStatusesForStage(stage.id)
                return [stage.id, Array.isArray(statusRes?.data) ? statusRes.data : []]
              } catch (err) {
                console.error(`❌ Error loading statuses for stage ${stage.id}:`, err)
                return [stage.id, []]
              }
            })
          )
          setStageStatusesMap(Object.fromEntries(entries))
        }
      } catch (err) { console.error('❌ Error loading stages:', err) }
    }

    const loadCountries = async () => {
      try {
        const res = await libraries.getCountries()
        if (res?.success) setCountryOptions(Array.isArray(res.data) ? res.data : [])
      } catch (err) { console.error('❌ Error loading countries:', err) }
    }

    const loadCities = async () => {
      try {
        const usedRes = await getUsedCityIds()
        const cityIds = usedRes?.data
        if (Array.isArray(cityIds) && cityIds.length) {
          const citiesRes = await libraries.getCitiesByIds(cityIds)
          const list = citiesRes?.data?.data ?? citiesRes?.data
          if (Array.isArray(list)) setCityOptions(list)
        } else {
          setCityOptions([])
        }
      } catch (err) {
        console.error('❌ Error loading cities:', err)
        try {
          const res = await libraries.getAllCities()
          if (res?.success) setCityOptions(Array.isArray(res.data) ? res.data : [])
        } catch (err2) { console.error('❌ Error loading cities fallback:', err2) }
      }
    }

    const loadTools = async () => {
      try {
        const res = await libraries.getTools()
        if (res?.success) setToolsOptions(Array.isArray(res.data) ? res.data : [])
      } catch (err) { console.error('❌ Error loading tools:', err) }
    }

    const loadProfessions = async () => {
      try {
        const [profRes, simRes] = await Promise.all([
          libraries.getProfessions(),
          libraries.getAllSimilarTerms()
        ])
        const profList = profRes?.success ? (Array.isArray(profRes.data) ? profRes.data : []) : []
        const simList = simRes?.success ? (Array.isArray(simRes.data) ? simRes.data : []) : []
        // Плоский список: MultiSelect не умеет группы — сгруппированные опции он
        // показывал бы как варианты выбора («Professions», «Similar Terms»).
        // Похожие термы помечаем «~», как они отображаются в самой таблице
        setProfessionFilterOptions([
          ...profList.map(p => ({ value: `prof_${p.id}`, label: p.name || p.value || `#${p.id}` })),
          ...simList.map(s => ({ value: `sim_${s.id}`, label: `~ ${s.value || s.name || `#${s.id}`}` }))
        ])
      } catch (err) { console.error('❌ Error loading professions:', err) }
    }

    const loadDepartments = async () => {
      try {
        const res = await libraries.getDepartments()
        if (res?.success) setDepartmentsOptions(Array.isArray(res.data) ? res.data : [])
      } catch (err) { console.error('❌ Error loading departments:', err) }
    }

    const loadLanguages = async () => {
      try {
        const res = await libraries.getLanguages()
        if (res?.success) setLanguageOptions(Array.isArray(res.data) ? res.data : [])
      } catch (err) { console.error('❌ Error loading languages:', err) }
    }

    const loadContactTools = async () => {
      try {
        const res = await libraries.getContactTools()
        if (res?.success) setContactToolsOptions(Array.isArray(res.data) ? res.data : [])
      } catch (err) { console.error('❌ Error loading contact tools:', err) }
    }

    const loadUsers = async () => {
      try {
        const res = await users.getAllUsers()
        if (res?.success) setUserOptions(Array.isArray(res.data) ? res.data : res.data?.users || [])
      } catch (err) { console.error('❌ Error loading users:', err) }
    }

    loadStatuses()
    loadStages()
    loadCountries()
    loadCities()
    loadTools()
    loadProfessions()
    loadDepartments()
    loadLanguages()
    loadContactTools()
    loadUsers()
  }

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filtering
    }))
  }

  const favoritesFilterActive = !!filters.favorites_of || filters.favorite_list_id.length > 0

  // Владелец и его списки меняются вместе: список прежнего владельца
  // новому не принадлежит и дал бы 403 на чужой list_id.
  const handleFavoritesFilterChange = ({ favorites_of, favorite_list_id }) => {
    setFilters(prev => ({
      ...prev,
      favorites_of: favorites_of || '',
      favorite_list_id: favorite_list_id || [],
      page: 1
    }))
  }

  /**
   * Сохранённый фильтр мог быть создан другим пользователем и нести его
   * favorites_of — без права на чужое избранное это дало бы 403, поэтому
   * подставляем свой id.
   */
  const applyForeignFavoritesGuard = (saved) => {
    if (!saved?.favorites_of) return saved
    if (String(saved.favorites_of) === String(user?.id) || canReadForeignFavorites) return saved
    return { ...saved, favorites_of: user?.id || '' }
  }

  const handleResetFilters = () => {
    setFilters({
      search: '',
      // Множественные фильтры — массивы
      status_id: [],
      stage_id: [],
      manager_id: [],
      profession_filter: [],
      department_id: [],
      tool_id: [],
      language_id: [],
      country_id: [],
      city_id: [],
      contact_tool_id: [],
      talent_type: [],
      note_type: [],
      important_date_type: [],
      // Одиночные: is_active — по сути да/нет, остальное текст и диапазоны
      is_active: defaultFilters.is_active !== undefined ? defaultFilters.is_active : '',
      current_only: defaultFilters.current_only !== undefined ? defaultFilters.current_only : '',
      hide_refusals: defaultFilters.hide_refusals !== undefined ? defaultFilters.hide_refusals : 'true',
      note_search: '',    // Очистка поиска по заметкам
      important_date_from: '',
      important_date_to: '',
      age_from: '',
      age_to: '',
      created_from: '',
      created_to: '',
      updated_from: '',
      updated_to: '',
      // Из defaultFilters, а не пустые: иначе Reset на странице «Избранное»
      // превратил бы её в общий список талантов.
      favorites_of: defaultFilters.favorites_of ?? '',
      favorite_list_id: defaultFilters.favorite_list_id ? [defaultFilters.favorite_list_id] : [],
      page: 1,
      limit: 20,
      sort_by: defaultFilters.sort_by ?? '',
      sort_order: defaultFilters.sort_order ?? 'ASC',
      include_relations: 'true'
    })
  }

  const handleDelete = (talent) => {
    setDeleteConfirm(talent)
  }

  const confirmDelete = async () => {
    try {
      await talentService.deleteTalent(deleteConfirm.id)
      setTalents(talents.filter(t => t.id !== deleteConfirm.id))
      setSuccessMessage(`${config.columnLabel} successfully deleted`)
      setTimeout(() => setSuccessMessage(''), 3000)
    } catch (err) {
      console.error(`Error deleting ${talentType}:`, err)
      setError(`Error deleting ${talentType}`)
      setTimeout(() => setError(''), 5000)
    }
  }

  const handleMerged = (report, source) => {
    // Дубликат удалён — убираем его из таблицы, если он был на текущей странице
    setTalents(talents.filter(t => t.id !== source.id))
    setMergeTarget(null)
    toast.success(`Merged ${[source.first_name, source.last_name].filter(Boolean).join(' ')} into ${[mergeTarget?.first_name, mergeTarget?.last_name].filter(Boolean).join(' ')}`)
  }

  const handleHistoryClick = (talent) => {
    setSelectedTalent(talent)
    setHistoryModalOpen(true)
  }

  // Status editing handlers
  const handleStatusEdit = (talentId) => {
    setEditingStatusId(talentId)
  }

  const handleStatusChange = async (talentId, newStatusId) => {
    if (!newStatusId || newStatusId === '' || newStatusId === '0') {
      handleStatusCancel()
      return
    }

    // Find the talent and its current status
    const talent = talents.find(t => t.id === talentId)
    if (!talent) return

    const currentStatus = talent.currentStatus || { id: null, name: 'No status', color: '#6B7280' }

    // If selecting the same status as current → cancel edit (normalize to string)
    const currentIdStr = currentStatus?.id != null ? String(currentStatus.id) : ''
    if (String(newStatusId) === currentIdStr) {
      handleStatusCancel()
      return
    }

    const newStatus = statusOptions.find(s => String(s.id) === String(newStatusId))
    if (!newStatus) {
      toast.error('Selected status is unavailable')
      handleStatusCancel()
      return
    }

    // Set loading state
    setStatusChangeLoading(talentId)

    // Open modal to check transition requirements
    setTransitionData({
      talentId: talentId,
      talentName: `${talent.first_name} ${talent.last_name}`,
      fromStatus: { id: currentStatus.id, name: currentStatus.name, color: currentStatus.color },
      toStatus: { id: newStatus.id, name: newStatus.name, color: newStatus.color }
    })
    setShowTransitionModal(true)
  }

  const handleStatusCancel = () => {
    setEditingStatusId(null)
    setStatusChangeLoading(null)
  }

  // Stage editing handlers — та же логика, что у статуса, но своя ось: без
  // проверки допустимого перехода (у стадий нет talent_status_transitions),
  // сама модалка решает нужна ли причина (движение назад по воронке)
  const handleStageEdit = (talentId) => {
    setEditingStageId(talentId)
  }

  const handleStageChange = (talentId, newStageId) => {
    if (!newStageId || newStageId === '' || newStageId === '0') {
      handleStageCancel()
      return
    }

    const talent = talents.find(t => t.id === talentId)
    if (!talent) return

    const currentStage = talent.currentStage || null
    const currentIdStr = currentStage?.id != null ? String(currentStage.id) : ''
    if (String(newStageId) === currentIdStr) {
      handleStageCancel()
      return
    }

    const newStage = stageOptions.find(s => String(s.id) === String(newStageId))
    if (!newStage) {
      toast.error('Selected stage is unavailable')
      handleStageCancel()
      return
    }

    setStageChangeLoading(talentId)
    setStageTransitionData({
      talentId,
      talentName: `${talent.first_name} ${talent.last_name}`,
      fromStage: currentStage,
      toStage: newStage
    })
    setShowStageTransitionModal(true)
  }

  const handleStageCancel = () => {
    setEditingStageId(null)
    setStageChangeLoading(null)
  }

  const handleStageTransitionSuccess = (newTalentData) => {
    setTalents(prev => prev.map(talent => {
      if (talent.id === stageTransitionData.talentId) {
        return {
          ...talent,
          currentStage: stageTransitionData.toStage,
          current_stage_id: stageTransitionData.toStage.id,
          ...newTalentData
        }
      }
      return talent
    }))

    toast.success(`Stage changed from "${stageTransitionData.fromStage?.name || 'No stage'}" to "${stageTransitionData.toStage.name}"`)

    setShowStageTransitionModal(false)
    setStageTransitionData(null)
    setEditingStageId(null)
    setStageChangeLoading(null)
  }

  const handleStageTransitionError = (error) => {
    console.error('Stage transition error:', error)
    const errorMessage = formatApiError(error, error.message || 'Error changing stage')
    toast.error(errorMessage)

    setShowStageTransitionModal(false)
    setStageTransitionData(null)
    setStageChangeLoading(null)
    setEditingStageId(null)
  }

  const handleTransitionSuccess = async (newTalentData) => {
    // Используем is_active из ответа API, если он есть, иначе вычисляем на основе статуса
    const newIsActive = newTalentData?.is_active !== undefined 
      ? newTalentData.is_active 
      : calculateIsActive(transitionData.toStatus)
    
    // Update talent data
    let updatedTalent = null
    setTalents(prev => prev.map(talent => {
      if (talent.id === transitionData.talentId) {
        updatedTalent = {
          ...talent,
          currentStatus: transitionData.toStatus,
          current_status_id: transitionData.toStatus.id,
          is_active: newIsActive,
          ...newTalentData
        }
        return updatedTalent
      }
      return talent
    }))

    // Синхронизируем статус активности с Auth User Service
    if (updatedTalent?.auth_user_id) {
      // Выполняем асинхронно, чтобы не блокировать основную операцию
      users.updateUserActivity(updatedTalent.auth_user_id, newIsActive).catch(err => {
        console.error('Failed to sync user activity status:', err)
      })
    }

    // Show success message
    toast.success(`Status changed from "${transitionData.fromStatus.name}" to "${transitionData.toStatus.name}"`)

    // Close modal and editing state
    setShowTransitionModal(false)
    setTransitionData(null)
    setEditingStatusId(null)
    setStatusChangeLoading(null)
  }

  const handleTransitionError = (error) => {
    console.error('Status transition error:', error)

    const errorMessage = formatApiError(error, error.message || 'Error changing status')
    toast.error(errorMessage)

    // Clear all related states and close modal to reset flow
    setShowTransitionModal(false)
    setTransitionData(null)
    setStatusChangeLoading(null)
    setEditingStatusId(null)
  }

  // Manager editing handlers
  const handleManagerEdit = (talentId) => {
    setEditingManagerId(talentId)
  }

  const handleManagerCancel = () => {
    setEditingManagerId(null)
    setManagerChangeLoading(null)
  }

  const handleManagerChange = async (talentId, managerId) => {
    // If empty string, treat as removing manager (set to null)
    const newManagerId = managerId === '' ? null : managerId;

    try {
      setManagerChangeLoading(talentId)

      // Update talent manager using dedicated endpoint
      await talentService.updateTalentManager(talentId, newManagerId)

      // Update the talents list with the new manager
      setTalents(prev => prev.map(talent => {
        if (talent.id === talentId) {
          return { ...talent, manager_id: newManagerId }
        }
        return talent
      }))

      setEditingManagerId(null)
      toast.success('Manager updated successfully', {
        duration: 2000,
        position: 'bottom-right',
      })
    } catch (err) {
      console.error('Error updating manager:', err)
      toast.error('Failed to update manager', {
        duration: 2000,
        position: 'bottom-right',
      })
    } finally {
      setManagerChangeLoading(null)
    }
  }

  // Linked user editing handlers
  const handleLinkedUserEdit = async (talentId) => {
    setEditingLinkedUserId(talentId)
    setLinkedUserOptions([])
    setLinkedUserOptionsLoading(true)
    try {
      const res = await talentService.getUsersNotInTalents(talentId)
      const list = res?.data || res?.users || (Array.isArray(res) ? res : [])
      setLinkedUserOptions(Array.isArray(list) ? list : [])
    } catch (err) {
      console.error('Error loading available users:', err)
      setLinkedUserOptions([])
    } finally {
      setLinkedUserOptionsLoading(false)
    }
  }

  const handleLinkedUserCancel = () => {
    setEditingLinkedUserId(null)
    setLinkedUserChangeLoading(null)
  }

  const handleLinkedUserChange = async (talentId, authUserId) => {
    const newAuthUserId = authUserId === '' ? null : authUserId
    try {
      setLinkedUserChangeLoading(talentId)
      await talentService.updateTalentLinkedUser(talentId, newAuthUserId)
      const newUser = userOptions.find(u => u.id === newAuthUserId) || null
      setTalents(prev => prev.map(talent => {
        if (talent.id === talentId) {
          return { ...talent, auth_user_id: newAuthUserId, authUser: newUser }
        }
        return talent
      }))
      setEditingLinkedUserId(null)
      toast.success('Linked user updated successfully', { duration: 2000, position: 'bottom-right' })
    } catch (err) {
      console.error('Error updating linked user:', err)
      toast.error('Failed to update linked user', { duration: 2000, position: 'bottom-right' })
    } finally {
      setLinkedUserChangeLoading(null)
    }
  }

  // Active status change handler
  const handleActiveStatusChange = async (talentId, newIsActive) => {
    try {
      setActiveStatusChangeLoading(talentId)

      // Update talent is_active status using dedicated endpoint
      await talentService.updateTalentActivity(talentId, newIsActive)

      // Update the talents list with the new is_active status
      let updatedTalent = null
      setTalents(prev => prev.map(talent => {
        if (talent.id === talentId) {
          updatedTalent = { ...talent, is_active: newIsActive }
          return updatedTalent
        }
        return talent
      }))

      // Синхронизируем статус активности с Auth User Service
      if (updatedTalent?.auth_user_id) {
        // Выполняем асинхронно, чтобы не блокировать основную операцию
        users.updateUserActivity(updatedTalent.auth_user_id, newIsActive).catch(err => {
          console.error('Failed to sync user activity status:', err)
        })
      }

      toast.success(`Talent ${newIsActive ? 'activated' : 'deactivated'} successfully`, {
        duration: 2000,
        position: 'bottom-right',
      })
    } catch (err) {
      console.error('Error updating active status:', err)
      toast.error('Failed to update active status', {
        duration: 2000,
        position: 'bottom-right',
      })
    } finally {
      setActiveStatusChangeLoading(null)
    }
  }

  const toggleChipsExpansion = (type, talentId) => {
    setExpandedChips(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [talentId]: !prev[type][talentId]
      }
    }))
  }

  // Copy contact to clipboard
  const copyContactToClipboard = async (contactValue) => {
    try {
      await navigator.clipboard.writeText(contactValue)
      toast.success(`Copied: ${contactValue}`, {
        duration: 2000,
        position: 'bottom-right',
      })
    } catch (err) {
      console.error('Failed to copy contact:', err)
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = contactValue
      document.body.appendChild(textArea)
      textArea.select()
      try {
        document.execCommand('copy')
        toast.success(`Copied: ${contactValue}`, {
          duration: 2000,
          position: 'bottom-right',
        })
      } catch (fallbackErr) {
        console.error('Fallback copy failed:', fallbackErr)
        toast.error('Failed to copy contact', {
          duration: 2000,
          position: 'bottom-right',
        })
      }
      document.body.removeChild(textArea)
    }
  }

  const handleSort = (columnKey) => {
    const sortKey = SORT_KEY_BY_COLUMN[columnKey] || columnKey
    setFilters(prev => {
      let newSortOrder = 'ASC'

      // If clicking the same column, change sort order
      if (prev.sort_by === sortKey) {
        newSortOrder = prev.sort_order === 'ASC' ? 'DESC' : 'ASC'
      }

      return {
        ...prev,
        sort_by: sortKey,
        sort_order: newSortOrder,
        page: 1 // Reset to first page when sorting
      }
    })
  }

  const handleCardSortByChange = (sortBy) => {
    setFilters(prev => ({
      ...prev,
      sort_by: sortBy,
      sort_order: 'ASC',
      page: 1
    }))
  }

  const handleCardSortOrderChange = (sortOrder) => {
    setFilters(prev => ({
      ...prev,
      sort_by: prev.sort_by || defaultCardSort.sortBy,
      sort_order: sortOrder,
      page: 1,
    }))
  }

  const handlePageChange = (page) => {
    setFilters(prev => ({
      ...prev,
      page
    }))
    scheduleResultsScrollToStart(resultsStartRef.current)
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified'

    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })
    } catch (error) {
      return 'Invalid date'
    }
  }

  // Смена типа идёт через тот же модал, что и в карточке: перевод меняет не
  // только тип, но и статус с talent_id, поэтому клик по чипу не должен
  // применяться молча
  const handleTypeChanged = async (newType) => {
    setPendingTypeChange(null)
    setError(null)

    // Перезапрашиваем список, а не выбрасываем строку: списки исторические,
    // и талант может остаться в этом же списке — уже с новым типом и статусом
    await fetchTalents()

    setSuccessMessage(`Talent successfully moved to ${getTalentTypeLabel(newType)}`)
    setTimeout(() => setSuccessMessage(''), 3000)
  }

  // Build columns: the static config lives at module scope (MEDIA_TYPE_COLUMNS);
  // only `config.columnLabel` is dynamic per talentType. renderCell/renderActions
  // close over component state, so columns rebuild when those state slices change.
  const columns = useMemo(() => {
    const rawColumns = [
      // alwaysPinned, а не обычная колонка: у пользователей с сохранённым порядком
      // колонок новая уехала бы в самый конец, за Actions. Закрепление держит звезду
      // слева рядом с именем; спрятать её через columnMenu по-прежнему можно
      // (это запрещает только alwaysVisible).
      // FavoriteStar подписан на FavoritesContext сам, поэтому состояние избранного
      // не входит в deps этого useMemo и клик не пересобирает таблицу.
      // Сортировка по дате добавления доступна только когда список отфильтрован
      // по избранному: иначе у большинства строк отметки нет и сортировать нечего.
      {
        key: 'favorite', label: '', align: 'center', alwaysPinned: true,
        width: 60, ariaLabel: 'Favorite', sortable: favoritesFilterActive
      },
      { key: 'talent', label: config.columnLabel, sortable: true, wrap: true, alwaysPinned: true, alwaysVisible: true },
      // Закреплена по умолчанию (см. useTableColumns ниже), но не alwaysPinned —
      // пользователь может открепить её через columnMenu.
      { key: 'id', label: 'ID', sortable: true },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'stage', label: 'Stage', sortable: true },
      { key: 'manager', label: 'Manager', sortable: false },
      { key: 'talent_id', label: 'Talent ID', sortable: true },
      { key: 'auth_user_id', label: 'Auth User', sortable: true },
      { key: 'contacts', label: 'Contacts', sortable: false },
      { key: 'professions', label: 'Professions', sortable: true },
      { key: 'tools', label: 'Tools', sortable: false },
      { key: 'responsibilities', label: 'Responsibilities', sortable: false },
      { key: 'languages', label: 'Languages', sortable: false },
      { key: 'public_slug', label: 'Slug', sortable: true },
      { key: 'public_short_name', label: 'Short Name', sortable: true },
      { key: 'public_rate', label: 'Rate', sortable: true },
      { key: 'public_seo_title', label: 'SEO Title', sortable: true },
      { key: 'public_seo_description', label: 'SEO Description', sortable: true },
      { key: 'public_slogan', label: 'Slogan', sortable: true },
      { key: 'public_is_pinned', label: 'Pinned', sortable: true },
      { key: 'location', label: 'Location', sortable: true },
      { key: 'birth_year', label: 'Age', sortable: true },
      ...MEDIA_TYPE_COLUMNS,
      { key: 'history', label: 'History', sortable: false },
      { key: 'active', label: 'Active', sortable: true, align: 'center' },
      // Без права на зарплату колонок нет вовсе, а не пустые ячейки.
      // Без сортировки: порядок строк выдавал бы зарплаты тем, кому их не видно
      ...(canReadSalary ? [
        { key: 'expectations_salary', label: 'Expectations Salary', sortable: false },
        { key: 'proposed_salary', label: 'Proposed Salary', sortable: false },
      ] : []),
      { key: 'actions', label: 'Actions', sortable: false, align: 'center' },
    ]
    return rawColumns.map((col) => ({
      ...col,
      render: col.key === 'actions'
        ? (talent) => renderActions(talent)
        : (talent) => renderCell(col, talent),
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    talentType, config.columnLabel, expandedChips, favoritesFilterActive,
    editingStatusId, statusChangeLoading,
    editingStageId, stageChangeLoading, stageOptions, stageStatusesMap,
    editingManagerId, managerChangeLoading, userOptions,
    editingLinkedUserId, linkedUserChangeLoading, linkedUserOptions, linkedUserOptionsLoading,
    activeStatusChangeLoading,
    canReadSalary, currencyById,
    // fromHref must be a dep so cell renderers re-capture it when filters
    // change; otherwise rows keep a stale ProfileLink `state.from`.
    fromHref,
  ])

  const talentRowKey = useCallback((talent) => talent.id, [])

  const sorting = useMemo(
    () => ({
      sort_by: COLUMN_BY_SORT_KEY[filters.sort_by] || filters.sort_by,
      sort_order: filters.sort_order
    }),
    [filters.sort_by, filters.sort_order]
  )

  const tableColumnsState = useTableColumns(columns, {
    ...(() => {
      const loaded = loadColumnsStateFromLS(
        `${talentType}_columns`,
        { defaultPinned: ['talent'], defaultHidden: ['birth_year'] },
        `${talentType}_visibleColumns`
      )
      // Stage — a column added after `status` existed. Users with a saved
      // order from before it existed would otherwise see it wherever the
      // table drops unknown columns (in practice, the far end) instead of
      // next to Status where it belongs.
      // ID belongs pinned right after the name. Until `id` shows up in the
      // saved order, seed it there and pin it; once it's saved, a manual unpin
      // sticks. Table leaves alwaysPinned keys like `talent` out of the order
      // it reports, so fall back to the front when there's nothing to anchor to.
      // Salary columns go right before Actions, not after it at the far end.
      const withStage = insertColumnBefore(
        insertColumnBefore(
          insertColumnAfter(loaded.defaultOrder, 'stage', 'status'),
          'expectations_salary', 'actions'
        ),
        'proposed_salary', 'actions'
      )
      if (withStage.includes('id')) return { ...loaded, defaultOrder: withStage }
      return {
        ...loaded,
        defaultOrder: withStage.includes('talent')
          ? insertColumnAfter(withStage, 'id', 'talent')
          : ['id', ...withStage],
        defaultPinned: loaded.defaultPinned.includes('id')
          ? loaded.defaultPinned
          : [...loaded.defaultPinned, 'id'],
      }
    })(),
    onChange: (state) => {
      try { localStorage.setItem(`${talentType}_columns`, JSON.stringify(state)) } catch {}
    },
  })

  // Custom cell renderer
  const renderCell = (column, talent) => {
    switch (column.key) {
      case 'favorite':
        return <FavoriteStar talentId={talent.id} />

      case 'talent': {
        const TypeIcon = getTalentTypeIcon(talent.talent_type)
        // Determine if this is a current or historical talent
        const isCurrentType = talent.talent_type === talentType
        const isHistoricalType = talent.type_category === 'historical' ||
                                       (talent.talent_type !== talentType && talent.was_type_at)

        return (
          <div className="flex items-start gap-3 min-w-[220px] text-left">
            <div className="shrink-0 p-[2px] rounded-full bg-gradient-to-br from-primary-400 to-purple-500 shadow-sm mt-0.5">
              <Avatar
                avatar={talent.authUser?.avatar}
                name={talent.authUser?.name}
                email={talent.authUser?.email}
                sizeClasses="h-9 w-9"
                iconSizeClasses="h-4 w-4"
                icon={TypeIcon}
                showInitialOnMissing={false}
                iconBgClassName="bg-light-bg-secondary dark:bg-dark-bg-tertiary"
                iconClassName="text-primary-600 dark:text-primary-400"
              />
            </div>
            <div className="min-w-0 flex flex-col items-start gap-1">
              <div className="min-w-0">
                <ProfileLink
                  to={`/talents/${talent.id}`}
                  state={{ from: fromHref }}
                  ariaLabel={`Open profile of ${talent.last_name} ${talent.first_name}`}
                  className="inline-block max-w-full break-words"
                >
                  {talent.last_name} {talent.first_name}
                </ProfileLink>
                <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                  ID: {talent.id}
                </div>
              </div>
              {isCurrentType ? (
                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                  Current {talentType}
                </span>
              ) : isHistoricalType ? (
                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                  {/* Текущий тип берём из самой карточки: перечисление типов
                      руками забывало trainee, и он показывался как «Former candidate» */}
                  {talent.talent_type && talent.talent_type !== talentType
                    ? `Former ${talentType} (${talent.talent_type})`
                    : `Former ${talentType}`}
                </span>
              ) : (
                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-light-bg-tertiary text-light-text-secondary dark:bg-dark-bg-tertiary dark:text-dark-text-secondary">
                  {getTalentTypeLabel(talent.talent_type) || 'Not defined'}
                </span>
              )}
              {talent.was_type_at && (
                <div className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
                  Was {talentType}: {formatDate(talent.was_type_at)}
                </div>
              )}
              {isHistoricalType && talent.talent_type !== talentType && (
                <div className="text-xs text-orange-600 dark:text-orange-400 font-medium">
                  Current type: {getTalentTypeLabel(talent.talent_type)}
                </div>
              )}
            </div>
          </div>
        )
      }

      case 'expectations_salary':
      case 'proposed_salary': {
        const isExpectations = column.key === 'expectations_salary'
        const amount = isExpectations ? talent.salary?.expectations_amount : talent.salary?.proposed_salary_amount
        const currencyId = isExpectations ? talent.salary?.expectations_currency_id : talent.salary?.proposed_salary_currency_id
        if (amount === null || amount === undefined || amount === '') {
          return <span className="text-light-text-tertiary dark:text-dark-text-tertiary">—</span>
        }
        const currency = currencyById[currencyId]
        return (
          <div className="text-sm text-light-text-primary dark:text-dark-text-primary whitespace-nowrap">
            {Number(amount).toLocaleString('en-US', { maximumFractionDigits: 2 })}
            {currency && (
              <span className="ml-1 text-light-text-secondary dark:text-dark-text-secondary" title={currency.name}>
                {currency.symbol || currency.name}
              </span>
            )}
          </div>
        )
      }

      case 'id':
        return (
          <div className="text-sm text-light-text-primary dark:text-dark-text-primary font-mono">
            {talent.id}
          </div>
        )

      case 'talent_id':
        return (
          <div className="text-sm text-light-text-primary dark:text-dark-text-primary font-mono">
            {talent.talent_id || <span className="text-light-text-tertiary dark:text-dark-text-tertiary">Not specified</span>}
          </div>
        )

      case 'auth_user_id':
        return (
          <UserSelect
            currentUser={talent.authUser || null}
            currentUserId={talent.auth_user_id}
            userOptions={editingLinkedUserId === talent.id ? linkedUserOptions : []}
            isEditing={editingLinkedUserId === talent.id}
            isLoading={linkedUserChangeLoading === talent.id || (editingLinkedUserId === talent.id && linkedUserOptionsLoading)}
            onEdit={hasPermission('talent-service', talent.talent_type, 'update') ? () => handleLinkedUserEdit(talent.id) : undefined}
            onCancel={handleLinkedUserCancel}
            onChange={(userId) => handleLinkedUserChange(talent.id, userId)}
          />
        )

      case 'contacts':
        if (!talent.contacts || talent.contacts.length === 0) {
          return <span className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">No contacts</span>
        }

        const isContactsExpanded = expandedChips.contacts[talent.id]
        const displayContacts = isContactsExpanded ? talent.contacts : talent.contacts.slice(0, 2)

        return (
          <div className="text-sm">
            <div className="space-y-1">
              {displayContacts.map((contact) => {
                const contactValue = typeof contact.value === 'object' ? contact.value?.value || 'Not specified' : contact.value || 'Not specified'

                return (
                  <div key={contact.id} className="flex items-center space-x-2">
                    {contact.tool && (
                      <span
                        className="inline-flex items-center cursor-help"
                        title={contact.tool.name}
                      >
                        {getToolIcon(contact.tool, { size: 'h-5 w-5' })}
                      </span>
                    )}
                    <div className="flex-1 min-w-0 flex items-center space-x-1">
                      <div
                        className="text-sm text-light-text-primary dark:text-dark-text-primary truncate max-w-[200px] cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                        onClick={() => copyContactToClipboard(contactValue)}
                        title={`Click to copy: ${contactValue}`}
                      >
                        {contactValue}
                      </div>
                      {contact.is_primary && (
                        <span className="ml-2 px-1 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs rounded flex-shrink-0">
                          Primary
                        </span>
                      )}
                      {contact.is_verified && (
                        <div className="text-green-500 dark:text-green-400 text-xs flex-shrink-0">✓</div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
            {talent.contacts.length > 2 && (
              <div className="mt-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleChipsExpansion('contacts', talent.id)
                  }}
                  className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                    isContactsExpanded
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50 focus:ring-blue-500 dark:focus:ring-blue-400'
                      : 'bg-light-bg-tertiary dark:bg-dark-bg-tertiary text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-bg-quaternary dark:hover:bg-dark-bg-quaternary focus:ring-gray-500 dark:focus:ring-gray-400'
                  }`}
                >
                  {isContactsExpanded ? (
                    <>
                      <span>Hide</span>
                      <svg className="ml-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </>
                  ) : (
                    <>
                      <span>+{talent.contacts.length - 2}</span>
                      <svg className="ml-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )

      case 'professions':
        if (!talent.professions || talent.professions.length === 0) {
          return <span className="text-light-text-tertiary dark:text-dark-text-tertiary text-sm">No professions</span>;
        }

        const isProfessionsExpanded = expandedChips.professions[talent.id];
        const displayProfessions = isProfessionsExpanded ? talent.professions : talent.professions.slice(0, 2);

        return (
          <div className="text-sm">
            <div className="space-y-1">
              {displayProfessions.map((profession) => (
                <div key={profession.id} className="flex items-center space-x-3">
                  <div className={`text-xs px-2 py-1 rounded flex-shrink-0 ${
                    profession.is_primary
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-light-bg-tertiary text-light-text-secondary dark:bg-dark-bg-tertiary dark:text-dark-text-secondary'
                  }`}>
                    {profession.is_primary ? 'Primary' : 'Additional'}
                  </div>
                  <div className="text-sm text-light-text-primary dark:text-dark-text-primary truncate max-w-32">
                    <div className="font-medium">{getProfessionName(profession)}</div>
                    <div className="text-xs text-light-text-secondary dark:text-dark-text-secondary">{getPositionName(profession)}</div>
                    {getSimilarTermName(profession) && (
                      <div className="text-xs text-purple-600 dark:text-purple-400 mt-0.5">
                        ~ {getSimilarTermName(profession)}
                      </div>
                    )}
                  </div>
                  {profession.public_visible && (
                    <div className="text-blue-500 dark:text-blue-400 text-xs flex-shrink-0">👁</div>
                  )}
                </div>
              ))}
            </div>
            {talent.professions.length > 2 && (
              <div className="mt-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleChipsExpansion('professions', talent.id);
                  }}
                  className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                    isProfessionsExpanded
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50 focus:ring-blue-500 dark:focus:ring-blue-400'
                      : 'bg-light-bg-tertiary dark:bg-dark-bg-tertiary text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-bg-quaternary dark:hover:bg-dark-bg-quaternary focus:ring-gray-500 dark:focus:ring-gray-400'
                  }`}
                >
                  {isProfessionsExpanded ? (
                    <>
                      <span>Hide</span>
                      <svg className="ml-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </>
                  ) : (
                    <>
                      <span>+{talent.professions.length - 2}</span>
                      <svg className="ml-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        );

      case 'tools':
        if (!talent.tools || talent.tools.length === 0) {
          return <span className="text-light-text-tertiary dark:text-dark-text-tertiary text-sm">No tools</span>;
        }

        const isToolsExpanded = expandedChips.tools[talent.id];
        const displayTools = isToolsExpanded ? talent.tools : talent.tools.slice(0, 3);

        return (
          <div className="text-sm">
            <div className="flex flex-wrap gap-1">
              {displayTools.map((tool) => (
                <span
                  key={tool.id}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400"
                >
                  {tool.tool?.icon && (
                    <img
                      src={tool.tool.icon}
                      alt={tool.tool.name || 'Tool icon'}
                      className="w-4 h-4 rounded mr-1"
                      onError={(e) => { e.target.style.display = 'none' }}
                    />
                  )}
                  {tool.tool?.name || 'Not specified'}
                </span>
              ))}
            </div>
            {talent.tools.length > 3 && (
              <div className="mt-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleChipsExpansion('tools', talent.id);
                  }}
                  className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                    isToolsExpanded
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50 focus:ring-blue-500 dark:focus:ring-blue-400'
                      : 'bg-light-bg-tertiary dark:bg-dark-bg-tertiary text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-bg-quaternary dark:hover:bg-dark-bg-quaternary focus:ring-gray-500 dark:focus:ring-gray-400'
                  }`}
                >
                  {isToolsExpanded ? (
                    <>
                      <span>Hide</span>
                      <svg className="ml-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </>
                  ) : (
                    <>
                      <span>+{talent.tools.length - 3}</span>
                      <svg className="ml-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        );

      case 'responsibilities': {
        if (!talent.responsibilities || talent.responsibilities.length === 0) {
          return <span className="text-light-text-tertiary dark:text-dark-text-tertiary text-sm">No responsibilities</span>;
        }

        const isResponsibilitiesExpanded = expandedChips.responsibilities[talent.id];
        const displayResponsibilities = isResponsibilitiesExpanded ? talent.responsibilities : talent.responsibilities.slice(0, 3);

        return (
          <div className="text-sm">
            <div className="flex flex-wrap gap-1">
              {displayResponsibilities.map((r) => (
                <span
                  key={r.id}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400"
                >
                  {r.responsibility?.name || `#${r.responsibility_id}`}
                </span>
              ))}
            </div>
            {talent.responsibilities.length > 3 && (
              <div className="mt-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleChipsExpansion('responsibilities', talent.id);
                  }}
                  className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                    isResponsibilitiesExpanded
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50 focus:ring-blue-500 dark:focus:ring-blue-400'
                      : 'bg-light-bg-tertiary dark:bg-dark-bg-tertiary text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-bg-quaternary dark:hover:bg-dark-bg-quaternary focus:ring-gray-500 dark:focus:ring-gray-400'
                  }`}
                >
                  {isResponsibilitiesExpanded ? (
                    <>
                      <span>Hide</span>
                      <svg className="ml-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </>
                  ) : (
                    <>
                      <span>+{talent.responsibilities.length - 3}</span>
                      <svg className="ml-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        );
      }

      case 'languages':
        if (!talent.languages || talent.languages.length === 0) {
          return <span className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">No languages</span>
        }

        const isLanguagesExpanded = expandedChips.languages[talent.id]
        const displayLanguages = isLanguagesExpanded ? talent.languages : talent.languages.slice(0, 2)

        return (
          <div className="text-sm">
            <div className="space-y-1">
              {displayLanguages.map((languageItem, index) => (
                <div key={languageItem.id || index} className="flex items-center space-x-2">
                  <div className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 flex items-center space-x-1">
                    <Languages className="h-3 w-3" />
                    <span>{languageItem.language?.name || 'Unknown'}</span>
                  </div>
                  {languageItem.level && (
                    <div className="text-xs px-1 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                      {languageItem.level.short_name || languageItem.level.name}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {talent.languages.length > 2 && (
              <div className="mt-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleChipsExpansion('languages', talent.id)
                  }}
                  className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                    isLanguagesExpanded
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50 focus:ring-blue-500 dark:focus:ring-blue-400'
                      : 'bg-light-bg-tertiary dark:bg-dark-bg-tertiary text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-bg-quaternary dark:hover:bg-dark-bg-quaternary focus:ring-gray-500 dark:focus:ring-gray-400'
                  }`}
                >
                  {isLanguagesExpanded ? (
                    <>
                      <span>Hide</span>
                      <svg className="ml-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </>
                  ) : (
                    <>
                      <span>+{talent.languages.length - 2}</span>
                      <svg className="ml-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )

      case 'location':
        return (
          <div className="text-sm text-light-text-primary dark:text-dark-text-primary">
            {talent.country ? (
              <div>
                <div className="font-medium">{talent.country.name}</div>
                {talent.city && (
                  <div className="text-xs text-light-text-secondary dark:text-dark-text-secondary">{talent.city.name}</div>
                )}
              </div>
            ) : (
              <span className="text-light-text-tertiary dark:text-dark-text-tertiary">Not specified</span>
            )}
          </div>
        )

      case 'birth_year':
        if (!talent.birth_year) return <span className="text-light-text-tertiary dark:text-dark-text-tertiary">—</span>
        return (
          <span className="text-sm text-light-text-primary dark:text-dark-text-primary">
            {new Date().getFullYear() - talent.birth_year} ({talent.birth_year})
          </span>
        )

      case 'status': {
        // Статусы, реально допустимые на текущей стадии таланта — не весь
        // список по типу. Карта собрана разом при загрузке страницы
        // (fetchReferenceData), поэтому здесь просто читаем по id стадии.
        const stageStatuses = talent.currentStage ? stageStatusesMap[talent.currentStage.id] : null
        return (
          <StatusSelect
            currentStatus={talent.currentStatus}
            statusOptions={
              stageStatuses ??
              (talentType === 'all'
                ? // Стадии ещё не загрузились (доля секунды) — как раньше, по типу
                  statusOptions.filter((status) => getStatusTypes(status).includes(talent.talent_type))
                : statusOptions)
            }
            isEditing={editingStatusId === talent.id}
            isLoading={statusChangeLoading === talent.id}
            onEdit={() => handleStatusEdit(talent.id)}
            onCancel={handleStatusCancel}
            onChange={(statusId) => handleStatusChange(talent.id, statusId)}
          />
        )
      }

      case 'stage':
        return (
          <StageSelect
            currentStage={talent.currentStage}
            stageOptions={stageOptions}
            isEditing={editingStageId === talent.id}
            isLoading={stageChangeLoading === talent.id}
            onEdit={() => handleStageEdit(talent.id)}
            onCancel={handleStageCancel}
            onChange={(stageId) => handleStageChange(talent.id, stageId)}
          />
        )

      case 'active':
        const isLoadingActive = activeStatusChangeLoading === talent.id
        const isActive = talent.is_active !== undefined ? talent.is_active : true

        return (
          <div className="flex items-center justify-center gap-2">
            <Toggle
              checked={isActive}
              isDisabled={isLoadingActive}
              onChange={(checked) => handleActiveStatusChange(talent.id, checked)}
              color="primary"
              label={
                <span className={`text-sm font-medium ${isActive ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}>
                  {isActive ? 'Active' : 'Inactive'}
                </span>
              }
            />
            {isLoadingActive && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
            )}
          </div>
        )

      case 'manager':
        const manager = userOptions.find(u => u.id === talent.manager_id)
        return (
          <ManagerSelect
            currentManager={manager}
            currentManagerId={talent.manager_id}
            managerOptions={userOptions}
            isEditing={editingManagerId === talent.id}
            isLoading={managerChangeLoading === talent.id}
            onEdit={() => handleManagerEdit(talent.id)}
            onCancel={handleManagerCancel}
            onChange={(managerId) => handleManagerChange(talent.id, managerId)}
          />
        )

      case 'history':
        return talent.events && talent.events.length > 0 ? (
          <button
            onClick={() => handleHistoryClick(talent)}
            className="text-left w-full hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary p-2 rounded transition-colors group"
          >
            <div className="text-sm text-light-text-primary dark:text-dark-text-primary">
              <div className="flex items-center">
                <Clock className="h-4 w-4 text-light-text-tertiary dark:text-dark-text-tertiary mr-2 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
                <span className="group-hover:text-blue-600 dark:group-hover:text-blue-400 font-medium">
                  {talent.events.length} events
                </span>
              </div>
              <div className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-1 group-hover:text-blue-600 dark:group-hover:text-blue-400">
                Last: {formatDate(talent.events[0]?.occurred_at)}
              </div>
            </div>
          </button>
        ) : (
          <span className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">No events</span>
        )

      case 'public_slug':
        return (
          <span className="text-sm text-light-text-primary dark:text-dark-text-primary font-mono truncate max-w-[120px] block" title={talent.publicSettings?.slug}>
            {talent.publicSettings?.slug || <span className="text-light-text-tertiary dark:text-dark-text-tertiary">—</span>}
          </span>
        )

      case 'public_short_name':
        return (
          <span className="text-sm text-light-text-primary dark:text-dark-text-primary truncate max-w-[120px] block" title={talent.publicSettings?.short_name}>
            {talent.publicSettings?.short_name || <span className="text-light-text-tertiary dark:text-dark-text-tertiary">—</span>}
          </span>
        )

      case 'public_rate':
        return (
          <span className="text-sm text-light-text-primary dark:text-dark-text-primary">
            {talent.publicSettings?.rate != null ? parseFloat(talent.publicSettings.rate).toFixed(2) : <span className="text-light-text-tertiary dark:text-dark-text-tertiary">—</span>}
          </span>
        )

      case 'public_seo_title':
        return (
          <span className="text-sm text-light-text-primary dark:text-dark-text-primary truncate max-w-[150px] block" title={talent.publicSettings?.seo_title}>
            {talent.publicSettings?.seo_title || <span className="text-light-text-tertiary dark:text-dark-text-tertiary">—</span>}
          </span>
        )

      case 'public_seo_description':
        return (
          <span className="text-sm text-light-text-primary dark:text-dark-text-primary truncate max-w-[200px] block" title={talent.publicSettings?.seo_description}>
            {talent.publicSettings?.seo_description || <span className="text-light-text-tertiary dark:text-dark-text-tertiary">—</span>}
          </span>
        )

      case 'public_slogan':
        return (
          <span className="text-sm text-light-text-primary dark:text-dark-text-primary truncate max-w-[200px] block" title={talent.publicSettings?.slogan}>
            {talent.publicSettings?.slogan || <span className="text-light-text-tertiary dark:text-dark-text-tertiary">—</span>}
          </span>
        )

      case 'public_is_pinned':
        return (
          <span className="text-sm text-light-text-primary dark:text-dark-text-primary">
            {talent.publicSettings?.is_pinned
              ? <span className="text-green-600 dark:text-green-400 font-medium">Yes</span>
              : <span className="text-light-text-tertiary dark:text-dark-text-tertiary">—</span>
            }
          </span>
        )

      default:
        if (column.key?.startsWith('media_')) {
          const mediaType = column.key.replace('media_', '')
          const mediaOfType = (talent.media || []).filter((m) => m.media_type === mediaType)
          if (mediaOfType.length === 0) {
            return <span className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">—</span>
          }
          const getMediaUrl = (m) => {
            // Используем file_url если есть (полный URL с бэкенда)
            if (m.file_url && (m.file_url.startsWith('http') || m.file_url.startsWith('file') || m.file_url.startsWith('smb'))) return m.file_url
            const path = m.file_path || m.url
            if (!path) return null
            // Внешние URL (http, https, file://, smb://) — используем как есть
            if (/^(https?|file|smb):\/\//i.test(path)) return path
            // Относительный путь — добавляем origin
            return `${window.location.origin}${path.startsWith('/') ? '' : '/'}${path}`
          }
          return (
            <div className="text-sm text-light-text-primary dark:text-dark-text-primary">
              {mediaOfType.map((m, idx) => {
                const url = getMediaUrl(m)
                const fullPath = url || m.file_path || ''
                return (
                  <span key={m.id || idx}>
                    {idx > 0 && ' '}
                    {url ? (
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-primary-600 dark:text-primary-400 hover:underline"
                        title={fullPath}
                      >
                        <ExternalLink className="h-4 w-4" aria-label="Open in new tab" />
                      </a>
                    ) : (
                      <span className="text-light-text-tertiary dark:text-dark-text-tertiary">—</span>
                    )}
                  </span>
                )
              })}
            </div>
          )
        }
        return null
    }
  }

  // Custom actions renderer.
  // Две строки: сверху действия над карточкой, снизу — переводы между типами.
  // Одной строкой семь однотипных иконок сливались и читались наугад.
  const renderActions = (talent, includeView = true) => {
    // Набор переходов берём от ТЕКУЩЕГО типа карточки, а не от страницы:
    // списки исторические, и в «Candidates» висят те, кто уже trainee или
    // employee — от страницы им доставались чужие кнопки, а вернуться в
    // candidate было нечем. В режиме «all» переходы не показываем.
    const typeTransitions = talentType !== 'all'
      ? Object.entries(TALENT_TYPE_CONFIG[talent.talent_type]?.typeTransitions || {})
      : []

    return (
      <div className="flex flex-col items-center gap-1.5">
        <div className="flex items-center justify-center space-x-2">
          {includeView && (
            <Link
              to={`/talents/${talent.id}`}
              state={{ from: fromHref }}
              className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
              title="View"
            >
              <Eye className="h-4 w-4" />
            </Link>
          )}
          {hasPermission('talent-service', talent.talent_type, 'update') && (
            <Link
              to={`/talents/${talent.id}/edit`}
              className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
              title="Edit"
            >
              <Edit className="h-4 w-4" />
            </Link>
          )}
          {hasPermission('talent-service', talent.talent_type, 'delete') && (
            <button
              onClick={() => setMergeTarget(talent)}
              className="text-amber-600 hover:text-amber-900 dark:text-amber-400 dark:hover:text-amber-300"
              title="Merge duplicate into this profile"
            >
              <Merge className="h-4 w-4" />
            </button>
          )}
          {hasPermission('talent-service', talent.talent_type, 'delete') && (
            <button
              onClick={() => handleDelete(talent)}
              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Переводы между типами — текстом: иконки типов (шапочка/книга/портфель/
            стрелка) на 16px неотличимы, приходилось наводить мышь ради тултипа */}
        {typeTransitions.length > 0 && (
          <div className="flex flex-wrap items-center justify-center gap-1 w-full pt-1.5 border-t border-light-border-primary dark:border-dark-border-primary">
            {typeTransitions.map(([type, transition]) => (
              <button
                key={type}
                onClick={() => setPendingTypeChange({ talent, type, reason: transition.reason })}
                className={`px-2 py-0.5 text-xs font-semibold rounded-full whitespace-nowrap transition-opacity hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed ${getTalentTypeColor(type)}`}
                title={transition.label}
              >
                To {getTalentTypeLabel(type)}
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  const EmptyIcon = config.emptyIcon

  const cardSortOptions = getTalentCardSortOptions({
    nameLabel: config.columnLabel,
    includeFavorite: favoritesFilterActive,
    includeStatusUpdated: talentType !== 'all',
  })
  const effectiveCardSort = getEffectiveTalentCardSort(filters.sort_by, filters.sort_order, defaultCardSort)

  const renderPagination = (sticky = false) => (
    <TalentListPagination
      pagination={pagination}
      limit={filters.limit}
      onPageChange={handlePageChange}
      onLimitChange={(limit) => handleFilterChange('limit', limit)}
      sticky={sticky}
    />
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title={displayTitle}
        description={<>{displayDescription}{headerExtra}</>}
        actions={
          <>
            <TalentExport talents={talents} talentType={talentType} />
            <button
              onClick={() => setIsActivityFitOpen(true)}
              className="flex min-h-11 items-center gap-2 rounded-lg border border-light-border-primary bg-light-bg-tertiary px-4 py-2 text-light-text-primary transition-colors hover:bg-light-bg-quaternary dark:border-dark-border-primary dark:bg-dark-bg-tertiary dark:text-dark-text-primary dark:hover:bg-dark-bg-quaternary"
            >
              <Activity className="h-4 w-4" />
              <span>Activity Feed</span>
            </button>
            <Link
              to="/talents/new"
              className="inline-flex min-h-11 items-center rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:bg-primary-500 dark:hover:bg-primary-600"
            >
              <Plus className="mr-2 h-5 w-5" />
              {config.addButtonText}
            </Link>
          </>
        }
      />

      {/* Success and Error Messages */}
      {successMessage && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400 dark:text-green-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800 dark:text-green-200">{successMessage}</p>
            </div>
          </div>
        </div>
      )}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400 dark:text-red-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800 dark:text-red-200">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-light-bg-primary dark:bg-dark-bg-secondary rounded-lg shadow-sm border border-light-border-primary dark:border-dark-border-primary p-4">
        {/* Filter header — always visible */}
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary whitespace-nowrap">
            Filters
          </h3>

          {/* Search */}
          <div className="w-full min-w-0 max-w-none sm:flex-1 sm:min-w-[180px] sm:max-w-[260px]">
            <SearchInput
              value={filters.search}
              onChange={(value) => handleFilterChange('search', value)}
              placeholder="Search by name, email or skills..."
              debounceTime={300}
              showIcon={true}
            />
          </div>

          {/* Status */}
          <div className="w-full min-w-0 sm:w-auto sm:min-w-[150px]">
            <Autocomplete
              multiple
              clearable
              fullWidth
              options={Array.isArray(statusOptions) ? statusOptions
                .filter((status) =>
                  talentType === 'all' && filters.talent_type?.length > 0
                    ? getStatusTypes(status).some((type) => filters.talent_type.includes(type))
                    : true
                )
                .map((status) => ({
                  value: String(status.id),
                  label: status.name
                })) : []}
              value={filters.status_id.map(String)}
              onChange={(value) => handleFilterChange('status_id', (value || []).map(Number))}
              placeholder="Status..."
              disabled={statusOptions.length === 0}
            />
          </div>

          {/* Stage — отдельная ось от статуса: "где талант находится" */}
          <div className="w-full min-w-0 sm:w-auto sm:min-w-[150px]">
            <Autocomplete
              multiple
              clearable
              fullWidth
              options={Array.isArray(stageOptions) ? stageOptions.map((stage) => ({
                value: String(stage.id),
                label: stage.name
              })) : []}
              value={filters.stage_id.map(String)}
              onChange={(value) => handleFilterChange('stage_id', (value || []).map(Number))}
              placeholder="Stage..."
              disabled={stageOptions.length === 0}
            />
          </div>

          {/* Manager */}
          <div className="w-full min-w-0 sm:w-auto sm:min-w-[150px]">
            <Autocomplete
              multiple
              clearable
              fullWidth
              options={Array.isArray(userOptions) ? userOptions.map((user) => ({
                value: String(user.id),
                label: user.name || user.email
              })) : []}
              value={filters.manager_id.map(String)}
              onChange={(value) => handleFilterChange('manager_id', value || [])}
              placeholder="Manager..."
              disabled={userOptions.length === 0}
            />
          </div>

          {/* Department */}
          <div className="w-full min-w-0 sm:w-auto sm:min-w-[150px]">
            <Autocomplete
              multiple
              clearable
              fullWidth
              options={Array.isArray(departmentsOptions) ? departmentsOptions.map((dept) => ({
                value: String(dept.id),
                label: dept.name
              })) : []}
              value={filters.department_id.map(String)}
              onChange={(value) => handleFilterChange('department_id', (value || []).map(Number))}
              placeholder="Department..."
              disabled={departmentsOptions.length === 0}
            />
          </div>

          {/* Hide Reject — только на стадийных страницах (/candidates/:stageKey):
              по умолчанию скрывает Denied/Refused/Not Approved/Blacklist/Not
              relevant for us, т.к. на конкретной стадии воронки они больше не
              интересны, но остаются в системе — просто не в этом списке.
              Вынесена в основной (всегда видимый) ряд фильтров, а не в "More":
              владелец ожидает переключать её часто. */}
          {stageKeys && (
            <Toggle
              checked={filters.hide_refusals !== 'false'}
              onChange={(checked) => handleFilterChange('hide_refusals', checked ? 'true' : 'false')}
              color="primary"
              label={
                <span className="text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary whitespace-nowrap">
                  Hide Reject
                </span>
              }
            />
          )}

          {/* Favorites: чьё избранное и какие его списки */}
          {!isFavoritesPage && (
            <FavoritesFilterDropdown
              favoritesOf={filters.favorites_of}
              favoriteListIds={filters.favorite_list_id}
              onChange={handleFavoritesFilterChange}
              currentUserId={user?.id}
              canReadForeign={canReadForeignFavorites}
              className="ml-auto"
            />
          )}

          {/* Saved filters */}
          <SavedFiltersDropdown
            pageKey={PAGE_KEYS.TALENTS_LIST}
            currentFilters={filters}
            applyKeys={SAVED_FILTER_KEYS}
            onApply={(saved) => setFilters(prev => ({ ...prev, ...normalizeMultiFilters(applyForeignFavoritesGuard(saved)), page: 1 }))}
            className={isFavoritesPage ? 'ml-auto' : ''}
          />

          {/* More / Less toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-1 text-sm text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary whitespace-nowrap"
          >
            <Filter className="w-4 h-4" />
            {showFilters ? 'Less' : 'More'}
            {showFilters ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Talent Type - only for "all" mode */}
              {talentType === 'all' && (
                <Autocomplete
                  multiple
                  clearable
                  fullWidth
                  label="Talent Type"
                  options={getTalentTypeOptions()}
                  value={filters.talent_type}
                  onChange={(value) => handleFilterChange('talent_type', value || [])}
                  placeholder="Select talent type..."
                />
              )}

              {/* Profession */}
              <Autocomplete
                multiple
                clearable
                fullWidth
                label="Profession"
                value={filters.profession_filter}
                onChange={(value) => handleFilterChange('profession_filter', value || [])}
                placeholder="Select profession..."
                options={professionFilterOptions}
              />

              {/* Tool */}
              <Autocomplete
                multiple
                clearable
                fullWidth
                label={`Tool (${toolsOptions.length})`}
                value={filters.tool_id.map(String)}
                onChange={(value) => handleFilterChange('tool_id', (value || []).map(Number))}
                placeholder="Select tool..."
                options={Array.isArray(toolsOptions) ? toolsOptions.map((tool) => ({
                  value: String(tool.id),
                  label: tool.name
                })) : []}
                disabled={toolsOptions.length === 0}
              />

              {/* Language */}
              <Autocomplete
                multiple
                clearable
                fullWidth
                label={`Language (${languageOptions.length})`}
                value={filters.language_id.map(String)}
                onChange={(value) => handleFilterChange('language_id', (value || []).map(Number))}
                placeholder="Select language..."
                options={Array.isArray(languageOptions) ? languageOptions.map((language) => ({
                  value: String(language.id),
                  label: language.name
                })) : []}
                disabled={languageOptions.length === 0}
              />

              {/* Contact Type */}
              <Autocomplete
                multiple
                clearable
                fullWidth
                label={`Contact Type (${contactToolsOptions.length})`}
                value={filters.contact_tool_id.map(String)}
                onChange={(value) => handleFilterChange('contact_tool_id', (value || []).map(Number))}
                placeholder="Select contact type..."
                options={Array.isArray(contactToolsOptions) ? contactToolsOptions.map((tool) => ({
                  value: String(tool.id),
                  label: tool.name
                })) : []}
                disabled={contactToolsOptions.length === 0}
              />

              {/* Country */}
              <Autocomplete
                multiple
                clearable
                fullWidth
                label={`Country (${countryOptions.length})`}
                value={filters.country_id.map(String)}
                onChange={(value) => handleFilterChange('country_id', (value || []).map(Number))}
                placeholder="Select country..."
                options={Array.isArray(countryOptions) ? countryOptions.map((country) => ({
                  value: String(country.id),
                  label: country.name
                })) : []}
                disabled={countryOptions.length === 0}
              />

              {/* City */}
              <Autocomplete
                multiple
                clearable
                fullWidth
                label={`City (${cityOptions.length})`}
                value={filters.city_id.map(String)}
                onChange={(value) => handleFilterChange('city_id', (value || []).map(Number))}
                placeholder="Select city..."
                options={Array.isArray(cityOptions) ? cityOptions.map((city) => ({
                  value: String(city.id),
                  label: city.name ?? city.term_group?.MainTerm?.value ?? city.term_group?.name ?? `City #${city.id}`
                })) : []}
                disabled={cityOptions.length === 0}
              />

              {/* Active Status */}
              <Autocomplete
                clearable
                fullWidth
                label="Active Status"
                value={filters.is_active}
                onChange={(value) => handleFilterChange('is_active', value || '')}
                placeholder="All"
                options={[
                  { value: 'true', label: 'Active' },
                  { value: 'false', label: 'Inactive' }
                ]}
              />

              {/* Note Type Filter */}
              <Autocomplete
                multiple
                clearable
                fullWidth
                label="Note Type"
                value={filters.note_type}
                onChange={(value) => handleFilterChange('note_type', value || [])}
                options={noteTypeOptions}
                placeholder="Select note type..."
              />

              {/* Note Search */}
              <div>
                <label className="block text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">
                  Note Search
                </label>
                <SearchInput
                  value={filters.note_search}
                  onChange={(value) => handleFilterChange('note_search', value)}
                  placeholder="Search in notes..."
                  debounceTime={300}
                  showIcon={true}
                />
              </div>

              {/* Important Date Type */}
              <Autocomplete
                multiple
                clearable
                fullWidth
                label="Important Date Type"
                value={filters.important_date_type}
                onChange={(value) => handleFilterChange('important_date_type', value || [])}
                placeholder="Select date type..."
                options={getDateTypesForSelect().map((t) => ({ value: t.value, label: t.label }))}
              />

              {/* Important Dates Range */}
              <DateRangePicker
                label="Important Dates Range"
                startDate={filters.important_date_from || ''}
                endDate={filters.important_date_to || ''}
                onChange={({ startDate, endDate }) => {
                  setFilters(prev => ({
                    ...prev,
                    important_date_from: startDate || '',
                    important_date_to: endDate || '',
                    page: 1
                  }))
                }}
                placeholder="Select date range"
              />

              {/* Created Date Range */}
              <DateRangePicker
                label="Created Date Range"
                startDate={filters.created_from || ''}
                endDate={filters.created_to || ''}
                onChange={({ startDate, endDate }) => {
                  setFilters(prev => ({
                    ...prev,
                    created_from: startDate || '',
                    created_to: endDate || '',
                    page: 1
                  }))
                }}
                placeholder="Select date range"
              />

              {/* Updated Date Range */}
              <DateRangePicker
                label="Updated Date Range"
                startDate={filters.updated_from || ''}
                endDate={filters.updated_to || ''}
                onChange={({ startDate, endDate }) => {
                  setFilters(prev => ({
                    ...prev,
                    updated_from: startDate || '',
                    updated_to: endDate || '',
                    page: 1
                  }))
                }}
                placeholder="Select date range"
              />
            </div>

            {/* Age range filter */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <TextField
                  label="Age from"
                  size="small"
                  fullWidth
                  type="number"
                  value={filters.age_from}
                  onChange={(e) => handleFilterChange('age_from', e.target.value)}
                  placeholder="18"
                  min="1"
                  max="100"
                />
              </div>
              <div>
                <TextField
                  label="Age to"
                  size="small"
                  fullWidth
                  type="number"
                  value={filters.age_to}
                  onChange={(e) => handleFilterChange('age_to', e.target.value)}
                  placeholder="65"
                  min="1"
                  max="100"
                />
              </div>
            </div>

            <div className="flex items-center justify-end">
              <button
                type="button"
                onClick={handleResetFilters}
                className="text-sm text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary"
              >
                Reset All Filters
              </button>
            </div>
          </div>
        )}
      </div>

      <div ref={resultsStartRef} className="scroll-mt-20 flex min-w-0 flex-wrap items-center justify-end gap-3">
        <div className="flex w-full min-w-0 flex-wrap items-center justify-end gap-3 sm:w-auto sm:flex-1">
          {viewMode === VIEW_MODE.CARDS && (
            <CompactCardSort
              options={cardSortOptions}
              sortBy={effectiveCardSort.sortBy}
              sortOrder={effectiveCardSort.sortOrder}
              onSortByChange={handleCardSortByChange}
              onSortOrderChange={handleCardSortOrderChange}
            />
          )}
          <DataViewToggle value={viewMode} onChange={setViewMode} />
        </div>
      </div>

      {/* Talents list — keep the existing table and add a dedicated card branch.
          TalentListPage does not configure row/bulk selection in either view, so
          switching to cards cannot leave an active selection hidden. */}
      {error && !successMessage ? (
        <div className="bg-light-bg-primary dark:bg-dark-bg-secondary rounded-lg shadow p-6 text-center">
          <p className="text-red-600 dark:text-red-400">{error}</p>
          <button
            onClick={fetchTalents}
            className="mt-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 underline"
          >
            Try again
          </button>
        </div>
      ) : viewMode === VIEW_MODE.TABLE ? (
        <Table
          columns={columns}
          data={Array.isArray(talents) ? talents : []}
          isLoading={tableLoading}
          sorting={sorting}
          onSort={handleSort}
          rowKey={talentRowKey}
          stickyHeaderOffset={0}
          stickyFooterOffset={0}
          columnMenu
          {...tableColumnsState}
          emptyState={
            <div className="py-8 text-center text-light-text-tertiary dark:text-dark-text-tertiary">
              <EmptyIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="text-lg font-medium">{config.emptyText}</p>
              <p className="text-sm">Try changing your search parameters</p>
            </div>
          }
          footer={renderPagination()}
        />
      ) : (
        <div className="relative isolate min-w-0 space-y-4">
          {tableLoading ? (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" aria-label="Loading talents">
              {Array.from({ length: Math.min(filters.limit, 6) }, (_, index) => (
                <div key={index} className="h-72 animate-pulse rounded-xl bg-light-bg-tertiary dark:bg-dark-bg-tertiary" />
              ))}
            </div>
          ) : talents.length === 0 ? (
            <div className="py-8 text-center text-light-text-tertiary dark:text-dark-text-tertiary">
              <EmptyIcon className="mx-auto mb-3 h-12 w-12 opacity-50" />
              <p className="text-lg font-medium">{config.emptyText}</p>
              <p className="text-sm">Try changing your search parameters</p>
            </div>
          ) : (
            <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-2">
              {talents.map((talent) => (
                <TalentCard
                  key={talent.id}
                  talent={talent}
                  fromHref={fromHref}
                  manager={userOptions.find((option) => option.id === talent.manager_id)}
                  actions={renderActions(talent, false)}
                />
              ))}
            </div>
          )}
          {renderPagination(true)}
        </div>
      )}

      {/* History Modal */}
      <HistoryModal
        isOpen={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        talent={selectedTalent}
        events={selectedTalent?.events || []}
      />

      {/* Status Transition Modal */}
      <StatusTransitionModal
        isOpen={showTransitionModal}
        onClose={() => {
          setShowTransitionModal(false)
          setTransitionData(null)
          setEditingStatusId(null)
          setStatusChangeLoading(null)
        }}
        entityType="talent"
        entityId={transitionData?.talentId}
        entityName={transitionData?.talentName}
        fromStatus={transitionData?.fromStatus}
        toStatus={transitionData?.toStatus}
        onSuccess={handleTransitionSuccess}
        onError={handleTransitionError}
      />

      {/* Stage Transition Modal */}
      <StageTransitionModal
        isOpen={showStageTransitionModal}
        onClose={() => {
          setShowStageTransitionModal(false)
          setStageTransitionData(null)
          setEditingStageId(null)
          setStageChangeLoading(null)
        }}
        talentId={stageTransitionData?.talentId}
        talentName={stageTransitionData?.talentName}
        fromStage={stageTransitionData?.fromStage}
        toStage={stageTransitionData?.toStage}
        onSuccess={handleStageTransitionSuccess}
        onError={handleStageTransitionError}
      />
      <ConfirmDialog
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={confirmDelete}
        confirmStyle="danger"
        title={`Delete ${config.columnLabel}`}
        message={`Are you sure you want to delete this ${talentType}? This action cannot be undone.`}
        confirmText="Delete"
      />

      {mergeTarget && (
        <MergeTalentModal
          talent={mergeTarget}
          onClose={() => setMergeTarget(null)}
          onMerged={handleMerged}
        />
      )}

      {pendingTypeChange && (
        <TalentTypeChangeModal
          isOpen
          onClose={() => setPendingTypeChange(null)}
          talent={pendingTypeChange.talent}
          initialType={pendingTypeChange.type}
          initialReason={pendingTypeChange.reason}
          onSuccess={handleTypeChanged}
        />
      )}

      <ActivityFitPanel
        isOpen={isActivityFitOpen}
        onClose={() => setIsActivityFitOpen(false)}
        entityType="Talent"
        title={`Activity Feed — ${displayTitle}`}
        extraParams={{
          ...(talentType && talentType !== 'all' ? { talent_type: talentType } : {}),
          ...(statusNames ? { status_names: statusNames } : {}),
          ...(stageKeys ? { stage_keys: stageKeys } : {}),
          ...(defaultFilters.is_active ? { is_active: defaultFilters.is_active } : {}),
        }}
      />
    </div>
  )
}

export default TalentListPage


